import { useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider, useAuth } from '@shared/providers/AuthProvider';
import { QueryProvider } from '@shared/providers/QueryProvider';
import { ThemeProvider } from '@shared/providers/ThemeProvider';
import { DataverseOrgProvider } from '@shared/providers';
import {
  Button,
  Card,
  Title1,
  Body1,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { veracityColors } from '@shared/ui';
import { userActivityTracker } from '@shared/entities/user-activity';
import { router } from './routes/router';

userActivityTracker.setApp('vera-shell');

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: veracityColors.firstLight,
    padding: tokens.spacingHorizontalL,
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: '1px solid #e8d5bc',
  },
  logoContainer: {
    marginBottom: tokens.spacingVerticalL,
  },
  logoSymbol: {
    width: '80px',
    height: 'auto',
    display: 'block',
  },
  title: {
    marginBottom: tokens.spacingVerticalS,
    color: veracityColors.midnightCharcoal,
    textAlign: 'center',
  },
  titleAccent: {
    color: veracityColors.veracityOrange,
  },
  subtitle: {
    color: veracityColors.duskBlue,
    marginBottom: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  button: {
    width: '100%',
  },
  errorBar: {
    width: '100%',
    marginBottom: tokens.spacingVerticalL,
  },
  footer: {
    marginTop: tokens.spacingVerticalXXL,
    color: veracityColors.duskBlue,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
  },
});

function LoginPage() {
  const styles = useStyles();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setError(null);
    try {
      await login();
    } catch (err) {
      console.error('Login failed:', err);
      setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.logoContainer}>
          <img src="/daybreak-logo.svg" alt="Daybreak logo" className={styles.logoSymbol} />
        </div>
        <Title1 className={styles.title}>
          <span className={styles.titleAccent}>Vera</span>
        </Title1>
        <Body1 className={styles.subtitle}>
          Sign in with your organization account to continue
        </Body1>
        {error && (
          <MessageBar intent="error" className={styles.errorBar}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        <Button
          appearance="primary"
          size="large"
          className={styles.button}
          onClick={handleLogin}
        >
          Sign in with Microsoft
        </Button>
        <Body1 className={styles.footer}>
          Enhance care. Reduce costs. Make it easy.
        </Body1>
      </Card>
    </div>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: veracityColors.firstLight }}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        <LoginPage />
      </ThemeProvider>
    );
  }

  return (
    <QueryProvider>
      <DataverseOrgProvider appId="vera-shell" defaultOrg="prod">
        <ThemeProvider>
          <RouterProvider router={router} />
        </ThemeProvider>
      </DataverseOrgProvider>
    </QueryProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
